import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { BentoContact } from "schemas/dashboard";

import { generateInitials } from "utils/string";

import { OptedOutContact, UploadedBrand } from "../schema";
import styles from "./styles";

interface Props {
  brands: UploadedBrand[];
  wishlistBrandNames: string[];
  optedOutContacts: OptedOutContact[];
  collectionName: string;
  setCollectionName: (value: string) => void;
  selectedContacts: Record<number, BentoContact | null>;
  onSelectContact: (bentoBrandId: number, contact: BentoContact | null) => void;
  onBack: () => void;
  onSaveAndPitch: () => void;
  onJustSave: () => void;
  saving: boolean;
}

const obfuscateEmail = (email: string): string => {
  const at = email.indexOf("@");
  if (at <= 0) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length <= 1) return `${local}***${domain}`;
  return `${local[0]}***${domain}`;
};

const ContactPicker = ({
  brand,
  selected,
  onSelect,
}: {
  brand: UploadedBrand;
  selected: BentoContact | null | undefined;
  onSelect: (contact: BentoContact | null) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const contacts = brand.contacts ?? [];

  if (contacts.length === 0) {
    return (
      <Box sx={styles.contactEmpty}>
        <Typography sx={styles.contactEmptyText}>No contacts found</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        sx={styles.contactButton}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Box sx={styles.contactButtonContent}>
          <Box sx={styles.contactNameRow}>
            <Typography sx={styles.contactName}>
              {selected?.name || selected?.email || "Pick a contact"}
            </Typography>
            {selected && !selected.id ? (
              <Box sx={styles.yoursTag}>
                <Box
                  component="i"
                  className="fa-regular fa-users"
                  sx={styles.yoursTagIcon}
                />
                Yours
              </Box>
            ) : (
              <Box sx={styles.bentoTag}>
                <Box
                  component="i"
                  className="fa-solid fa-circle-check"
                  sx={styles.bentoTagIcon}
                />
                Contact Found
              </Box>
            )}
          </Box>
          {selected?.email && (
            <Typography sx={styles.contactEmail}>
              {obfuscateEmail(selected.email)}
            </Typography>
          )}
        </Box>
        <Box
          component="i"
          className="fa-regular fa-chevron-down"
          sx={styles.contactChevron}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: styles.contactMenu } }}
      >
        {contacts.map((contact) => (
          <MenuItem
            key={contact.id ?? contact.email}
            selected={selected?.id === contact.id}
            onClick={() => {
              onSelect(contact);
              setAnchorEl(null);
            }}
            sx={styles.contactMenuItem}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={styles.contactMenuName}>
                {contact.name || contact.email}
              </Typography>
              <Typography sx={styles.contactMenuEmail}>
                {contact.email}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ReviewBrands = ({
  brands,
  wishlistBrandNames,
  optedOutContacts,
  collectionName,
  setCollectionName,
  selectedContacts,
  onSelectContact,
  onBack,
  onSaveAndPitch,
  onJustSave,
  saving,
}: Props) => {
  const [wishlistExpanded, setWishlistExpanded] = useState(false);
  const contactCount = useMemo(
    () => brands.filter((b) => !!selectedContacts[b.bentoBrand.id]).length,
    [brands, selectedContacts],
  );

  const brandCount = brands.length;
  const wishlistCount = wishlistBrandNames.length;
  const trimmedName = collectionName.trim();
  const canSave = !saving && trimmedName.length > 0 && brandCount > 0;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Button disableRipple sx={styles.backButton} onClick={onBack}>
          <Box
            component="i"
            className="fa-regular fa-arrow-left"
            sx={styles.backIcon}
          />
          Edit list
        </Button>
        <Typography sx={styles.title}>Review & Select Contacts</Typography>
        <Box sx={{ width: 28 }} />
      </Box>

      <Box sx={styles.summary}>
        <Box sx={styles.summaryRow}>
          <Typography sx={styles.summaryCount}>{contactCount}</Typography>
          <Typography sx={styles.summaryLabel}>contacts</Typography>
          <Typography sx={styles.summaryMeta}>
            across {brandCount} {brandCount === 1 ? "brand" : "brands"}
          </Typography>
        </Box>
        {brandCount > 0 && (
          <Typography sx={styles.summarySubtitle}>
            Pick a contact per brand to pitch to
          </Typography>
        )}
      </Box>

      {wishlistCount > 0 && (
        <Box sx={styles.wishlistSection}>
          <Box
            component="button"
            type="button"
            sx={styles.wishlistNotice}
            onClick={() => setWishlistExpanded((expanded) => !expanded)}
            aria-expanded={wishlistExpanded}
            title={wishlistExpanded ? "Hide brands" : "Show brands"}
          >
            <Typography sx={styles.wishlistNoticeText}>
              {wishlistCount} {wishlistCount === 1 ? "brand" : "brands"} added
              to your wishlist
            </Typography>
            <Box
              component="i"
              className={`fa-regular ${
                wishlistExpanded ? "fa-chevron-up" : "fa-chevron-down"
              }`}
              sx={styles.wishlistNoticeChevron}
            />
          </Box>

          {wishlistExpanded && (
            <>
              <Typography sx={styles.wishlistSubtitle}>
                We're finding contacts for these brands for you.{" "}
              </Typography>
              <Box sx={styles.wishlistBrandScroll}>
                {wishlistBrandNames.map((name, index) => (
                  <Box key={`${name}-${index}`} sx={styles.wishlistBrandRow}>
                    <Box sx={styles.wishlistBrandIconCircle}>
                      <Box
                        component="i"
                        className="fa-regular fa-clock"
                        sx={styles.wishlistBrandIcon}
                      />
                    </Box>
                    <Typography sx={styles.wishlistBrandName}>
                      {name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {optedOutContacts.length > 0 && (
        <Box sx={styles.optedOutNotice}>
          <Box sx={styles.optedOutNoticeHeader}>
            <Box
              component="i"
              className="fa-regular fa-circle-exclamation"
              sx={styles.optedOutNoticeIcon}
            />
            <Typography sx={styles.optedOutNoticeText}>
              {optedOutContacts.length === 1
                ? "This contact has opted out of being contacted through Bento:"
                : "These contacts have opted out of being contacted through Bento:"}
            </Typography>
          </Box>
          <Box sx={styles.optedOutList}>
            {optedOutContacts.map((contact) => (
              <Typography
                key={`${contact.brandName}-${contact.email}`}
                sx={styles.optedOutItem}
              >
                <Box component="span" sx={styles.optedOutItemBrand}>
                  {contact.brandName}
                </Box>
                {" — "}
                {contact.email}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={styles.brandList}>
        {brandCount === 0 ? (
          <Typography sx={styles.emptyText}>
            No brands matched your upload.
          </Typography>
        ) : (
          brands.map((brand) => (
            <Box key={brand.bentoBrand.id} sx={styles.brandRow}>
              <Avatar
                src={brand.bentoBrand.logoUrl || undefined}
                sx={{
                  ...styles.brandAvatar,
                  backgroundColor: brand.bentoBrand.backgroundLogoColor
                    ? brand.bentoBrand.backgroundLogoColor
                    : "#f6c8d0",
                }}
              >
                {generateInitials(
                  brand.bentoBrand.brandName || brand.bentoBrand.normalizedName,
                )}
              </Avatar>
              <Box sx={styles.brandInfo}>
                <Typography sx={styles.brandName}>
                  {brand.bentoBrand.brandName ||
                    brand.bentoBrand.normalizedName}
                </Typography>
                {brand.bentoBrand.instagramUsername && (
                  <Typography sx={styles.brandHandle}>
                    @{brand.bentoBrand.instagramUsername}
                  </Typography>
                )}
              </Box>
              <Box sx={styles.divider} />
              <Box sx={styles.contactCell}>
                <ContactPicker
                  brand={brand}
                  selected={selectedContacts[brand.bentoBrand.id]}
                  onSelect={(contact) =>
                    onSelectContact(brand.bentoBrand.id, contact)
                  }
                />
              </Box>
              <IconButton
                size="small"
                sx={styles.removeButton}
                onClick={() => onSelectContact(brand.bentoBrand.id, null)}
                aria-label="Skip contact"
              >
                <Box
                  component="i"
                  className="fa-regular fa-xmark"
                  sx={styles.removeIcon}
                />
              </IconButton>
            </Box>
          ))
        )}
      </Box>

      <Box sx={styles.footer}>
        <TextField
          fullWidth
          size="small"
          placeholder="Name this list…"
          value={collectionName}
          onChange={(e) => setCollectionName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={styles.nameInput}
          InputProps={{ disableUnderline: true }}
        />
        <Button
          disabled={!canSave}
          sx={styles.saveAndPitchButton}
          onClick={onSaveAndPitch}
        >
          {saving ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            <Box
              component="i"
              className="fa-regular fa-paper-plane"
              sx={styles.saveAndPitchIcon}
            />
          )}
          Save & Start Pitching
        </Button>
        <Button
          disableRipple
          disabled={!canSave}
          sx={styles.justSaveButton}
          onClick={onJustSave}
        >
          Just save for later
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewBrands;
